import { useAppState } from 'hooks/useAppState';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { RawPermissions } from 'records/Permissions';
import { Project } from 'records/Project';

type ProjectPermissionsGuardProps = {
  /**
   * The content that should be rendered if the required
   * permissions are present.
   */
  children: React.ReactElement;

  /**
   * A set of permissions required to
   * view the content.
   */
  permissions: Array<keyof RawPermissions>;

  /**
   * The path, if any, the user should be redirected to if the
   * required permissions are missing.
   */
  redirectTo?: `/${string}`;
};

/**
 * A guard that requires a set of permissions to view
 * certain content.
 */
const ProjectPermissionsGuard: React.FC<ProjectPermissionsGuardProps> = ({
  children,
  permissions,
  redirectTo,
}) => {
  const { projects, selectedProject } = useAppState();
  const project = projects.get(selectedProject, new Project());

  const hasRequiredPermissions = permissions.every((permission) => {
    return project.permissions.get(permission, false);
  });

  if (hasRequiredPermissions) {
    return children;
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return null;
};

export default ProjectPermissionsGuard;
